import { FontWeight, formatCurrency, Slider, Typography, TypographyVariant, UseState } from '@elearning-platform/ui';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { PageDataContext } from '../../components/page';
import { PageData } from '../../interfaces';
import { CenteredContentSlideWrapper } from '../centeredContentSlideWrapper/centeredContentSlideWrapper.component';

import { MainContent, Segment, StyledMarkdown } from './insuranceCalculator.styled';
import { InsuranceCalculatorProps } from './insuranceCalculator.types';

export const InsuranceCalculator: React.FC<InsuranceCalculatorProps> = (props: InsuranceCalculatorProps): JSX.Element => {
  const {
    baseUrl,
    centeredContentSlideWrapperData,
    callComplete,
    completed,
  }: InsuranceCalculatorProps = props;
  const { t }: TransProps<never> = useTranslation();
  const dataCtx: Partial<PageData> | null = useContext(PageDataContext);
  const {
    slidesOverallCounter,
    currentSlideCounter,
    title,
    description,
  }: Partial<PageData> = dataCtx ?? {};
  const minLoanValue: number = 1000;
  const [months, setMonths]: UseState<number> = useState<number>(1);
  const [loanValue, setLoanValue]: UseState<number> = useState<number>(minLoanValue);
  const [monthsToDisplay, setMonthsToDisplay]: UseState<number> = useState<number>(1);
  const [loanValueToDisplay, setLoanValueToDisplay]: UseState<number> = useState<number>(minLoanValue);
  const insurancePremium: number = useMemo((): number => loanValue * .000629, [loanValue]);

  useEffect((): void => {
    if (!completed) {
      callComplete();
    }
  }, [completed, callComplete]);

  return (
    <CenteredContentSlideWrapper
      currentSlideCounter={currentSlideCounter}
      description={description}
      leftImageSrc={baseUrl + centeredContentSlideWrapperData.leftImageSrc}
      rightImageSrc={baseUrl + centeredContentSlideWrapperData.rightImageSrc}
      slidesOverallCounter={slidesOverallCounter}
      title={title}
    >
      <MainContent>
        <Segment>
          <Typography variant={TypographyVariant.TextSM}>
            <StyledMarkdown
              text={
                t('competence.app.scrollLesson.componentsV2.insuranceCalculator.durationInMonths', { months: monthsToDisplay })
              }
            />
          </Typography>
          <Slider maxValue={96} onSliderRelease={setMonths} onValueChange={setMonthsToDisplay} />
        </Segment>
        <Segment>
          <Typography variant={TypographyVariant.TextSM}>
            <StyledMarkdown
              text={t(
                'competence.app.scrollLesson.componentsV2.insuranceCalculator.loanValue',
                { value: formatCurrency(loanValueToDisplay, undefined, undefined, true) }
              )}
            />
          </Typography>
          <Slider
            maxValue={250000}
            minValue={minLoanValue}
            onSliderRelease={setLoanValue}
            onValueChange={setLoanValueToDisplay}
            step={minLoanValue}
          />
        </Segment>
        <Segment>
          <Typography variant={TypographyVariant.TextSM}>
            <StyledMarkdown
              text={t(
                'competence.app.scrollLesson.componentsV2.insuranceCalculator.totalInsurancePremium',
                { insurancePremium: formatCurrency(insurancePremium), months }
              )}
            />
          </Typography>
          <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.DisplayMD}>
            {formatCurrency(insurancePremium * months)}
          </Typography>
        </Segment>
      </MainContent>
      <Typography variant={TypographyVariant.Italic}>
        {t('competence.app.scrollLesson.componentsV2.insuranceCalculator.disclaimer')}
      </Typography>
    </CenteredContentSlideWrapper>
  );
};
